import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { NAV_ITEMS } from '@/constants/site'

interface SiteHeaderProps {
    variant?: 'overlay' | 'solid'
}

export const SiteHeader: FC<SiteHeaderProps> = ({ variant = 'solid' }): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 16)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        const onResize = (): void => {
            if (window.innerWidth >= 1024) setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', onResize)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', onResize)
        }
    }, [open])

    const transparent = variant === 'overlay' && !scrolled && !open
    const closeMenu = (): void => setOpen(false)

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300',
                transparent
                    ? 'bg-transparent'
                    : 'bg-white/90 shadow-[0_1px_0_rgba(11,27,52,0.06),0_12px_32px_-18px_rgba(11,27,52,0.25)] backdrop-blur-xl'
            )}
        >
            <div className='mx-auto flex h-18 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <Link
                    href='/'
                    aria-label='Jeme, ir al inicio'
                    className='rounded-full focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-brand-300'
                    onClick={closeMenu}
                >
                    <Logo tone={transparent ? 'light' : 'dark'} />
                </Link>

                <nav aria-label='Principal' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {NAV_ITEMS.map((item) => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    className={cn(
                                        'rounded-full px-4 py-2 text-[0.95rem] font-semibold transition-colors duration-200',
                                        transparent
                                            ? 'text-white/90 hover:bg-white/12 hover:text-white'
                                            : 'text-foreground/75 hover:bg-brand-50 hover:text-brand-700'
                                    )}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href='/#descargar'
                        className={cn(
                            'hidden h-11 items-center rounded-full px-5 text-sm font-bold transition duration-200 hover:-translate-y-0.5 sm:inline-flex',
                            transparent
                                ? 'bg-white text-brand-700 hover:bg-brand-50'
                                : 'bg-primary text-white shadow-[0_10px_20px_-10px_rgba(10,106,230,0.9)] hover:bg-brand-700'
                        )}
                    >
                        Descargar la app
                    </Link>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='menu-movil'
                        aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
                        onClick={() => setOpen((value) => !value)}
                        className={cn(
                            'grid size-11 place-items-center rounded-full transition-colors lg:hidden',
                            transparent
                                ? 'text-white hover:bg-white/12'
                                : 'text-foreground hover:bg-brand-50'
                        )}
                    >
                        {open ? <X className='size-6' /> : <Menu className='size-6' />}
                    </button>
                </div>
            </div>

            <div
                id='menu-movil'
                hidden={!open}
                className='border-t border-border bg-white lg:hidden'
            >
                <nav aria-label='Menú móvil' className='mx-auto max-w-7xl px-5 pt-3 pb-6 sm:px-8'>
                    <ul className='flex flex-col'>
                        {NAV_ITEMS.map((item) => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    onClick={closeMenu}
                                    className='flex h-12 items-center rounded-xl px-3 text-base font-semibold text-foreground transition-colors hover:bg-brand-50 hover:text-brand-700'
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <Link
                        href='/#descargar'
                        onClick={closeMenu}
                        className='mt-4 flex h-12 items-center justify-center rounded-full bg-primary text-base font-bold text-white transition-colors hover:bg-brand-700'
                    >
                        Descargar la app
                    </Link>
                </nav>
            </div>
        </header>
    )
}
